import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className = '',
}) => (
  <div
    className={`rounded-3xl border shadow-xl ${className}`}
    style={{
      background: 'rgba(255,255,255,0.75)',
      backdropFilter: 'blur(24px) saturate(1.2)',
      WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
      borderColor: 'rgba(255,255,255,0.6)',
      boxShadow: '0 8px 32px rgba(148,163,184,0.12)',
    }}
  >
    {children}
  </div>
);

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const userType = (searchParams.get('userType') as 'driver' | 'rider') || 'rider';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new link.');
    }
  }, [token]);

  const getPasswordStrength = (pw: string): { level: string; color: string; width: string } => {
    if (pw.length < 6) return { level: 'Too short', color: 'bg-red-400', width: 'w-1/5' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { level: 'Weak', color: 'bg-red-400', width: 'w-2/5' };
    if (score <= 3) return { level: 'Medium', color: 'bg-amber-400', width: 'w-3/5' };
    if (score <= 4) return { level: 'Strong', color: 'bg-emerald-400', width: 'w-4/5' };
    return { level: 'Very Strong', color: 'bg-emerald-500', width: 'w-full' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword, userType, ...(twoFactorCode ? { twoFactorCode } : {}) }),
      });
      const data = await response.json();
      if (data.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setError('');
        return;
      }
      if (!response.ok) {
        setError(data.error || 'Failed to reset password. The link may have expired.');
        return;
      }
      setSuccess(true);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const strength = getPasswordStrength(newPassword);

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-white/50 border border-slate-200/60 text-slate-800 placeholder-slate-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200/50 transition-all';

  if (success) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <GlassCard className="p-8 sm:p-10 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">Password Reset</h2>
            <p className="text-slate-500 mb-6">
              Your password has been updated. You can now log in.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3.5 bg-slate-800 text-white font-bold rounded-2xl shadow-lg hover:bg-slate-700 transition-all"
            >
              Go to Login
            </button>
          </GlassCard>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <GlassCard className="p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-slate-800 mb-1">Reset Password</h1>
            <p className="text-slate-500">Enter your new password below</p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-6 px-4 py-3 rounded-xl text-sm border text-rose-600"
              style={{
                background: 'rgba(255,241,242,0.8)',
                borderColor: 'rgba(254,205,211,0.5)',
              }}
            >
              {error}
            </div>
          )}

          {/* Form */}
          {token && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    className={`${inputClass} pr-12`}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-sm"
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-400">Strength</span>
                      <span className={
                        strength.level === 'Weak' || strength.level === 'Too short'
                          ? 'text-red-400'
                          : strength.level === 'Medium'
                          ? 'text-amber-500'
                          : 'text-emerald-500'
                      }>
                        {strength.level}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${strength.color} ${strength.width} rounded-full transition-all duration-300`} />
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  className={`${inputClass} ${
                    confirmPassword && confirmPassword !== newPassword
                      ? '!border-rose-300 focus:!border-rose-400 focus:!ring-rose-200/50'
                      : ''
                  }`}
                  placeholder="Confirm your password"
                  required
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-rose-400 text-xs mt-1">Passwords do not match</p>
                )}
              </div>

              {/* 2FA Code (shown if required) */}
              {requiresTwoFactor && (
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">
                    Two-Factor Authentication Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={twoFactorCode}
                    onChange={(e) => { setTwoFactorCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6)); setError(''); }}
                    className={inputClass}
                    placeholder="Enter 6-digit code from your authenticator app"
                    required
                    maxLength={6}
                  />
                  <p className="text-slate-400 text-xs mt-1">
                    Enter the code from your authenticator app, or a backup code
                  </p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword || (requiresTwoFactor && twoFactorCode.length < 6)}
                className="w-full py-3.5 bg-slate-800 text-white font-bold rounded-2xl shadow-lg shadow-slate-300/40 hover:bg-slate-700 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Resetting...' : requiresTwoFactor ? 'Verify & Reset' : 'Reset Password'}
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </GlassCard>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
